import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react';

import { useAssistant } from '@core/assistant/providers/Provider';
import { useMytaverseEvent } from '@core/dashboard/providers';

import type { IAssistantScript } from '@shared/interfaces/assistantScript';
import type { IAssistantRagVectorStorage } from '@shared/interfaces/assistantRagStorage';

import { useAllAssistantScripts } from './useAllAssistantScripts';
import { useAssistantRagStorage } from './useAssistantRagStorage';

export interface EditorAiData {
  scripts: IAssistantScript[];
  ragItems: IAssistantRagVectorStorage[];
  ragLoading: boolean;
  refetchScripts: () => Promise<void>;
  refetchRag: () => Promise<void>;
  // Single fan-out for mutations: refetches scripts, RAG, AND the assistant
  // runtime's script list (so playback stays in sync). Consumers should call
  // this after creating, updating, or deleting a script or knowledge item.
  refetchAll: () => Promise<void>;
}

const EditorAiDataContext = createContext<EditorAiData | null>(null);

interface EditorAiDataProviderProps {
  children: ReactNode;
}

/**
 * Owns a single source of truth for the editor's assistant scripts + RAG
 * items. Without this, the tree and the inspector each instantiate their own
 * `useAllAssistantScripts` / `useAssistantRagStorage` hooks, and a refetch
 * triggered by a mutation in one panel never reaches the other — so the user
 * sees a stale list after creating or editing a script/knowledge entry.
 */
export const EditorAiDataProvider = ({
  children,
}: EditorAiDataProviderProps) => {
  const { currentBrandingId, setAssistantScriptUpdateTick } =
    useMytaverseEvent();
  const assistant = useAssistant();
  const activeBrandingId =
    assistant?.activeBrandingId ?? currentBrandingId ?? undefined;

  const { scripts, refetch: refetchScripts } = useAllAssistantScripts();
  const {
    items: ragItems,
    refetch: refetchRag,
    loading: ragLoading,
  } = useAssistantRagStorage(activeBrandingId);

  const refetchAll = useCallback(async () => {
    await Promise.all([refetchScripts(), refetchRag()]);
    // The branding editor renders as a sibling of the assistant button, i.e.
    // OUTSIDE AssistantProvider, so `assistant.fetchScripts` here is a no-op
    // and the live Noa keeps a stale script list (e.g. an outdated
    // playbackPolicy) until reload. Bump the shared tick instead: the
    // AssistantProvider listens for it and refetches its scripts + clears
    // prerendered audio, so entering an area uses the just-saved script. This
    // is the same signal the WS ASSISTANT_SCRIPT_UPDATED event raises for
    // other clients.
    setAssistantScriptUpdateTick?.((prev) => prev + 1);
  }, [refetchScripts, refetchRag, setAssistantScriptUpdateTick]);

  const value = useMemo<EditorAiData>(
    () => ({
      scripts,
      ragItems,
      ragLoading,
      refetchScripts,
      refetchRag,
      refetchAll,
    }),
    [scripts, ragItems, ragLoading, refetchScripts, refetchRag, refetchAll],
  );

  return (
    <EditorAiDataContext.Provider value={value}>
      {children}
    </EditorAiDataContext.Provider>
  );
};

export const useEditorAiData = (): EditorAiData => {
  const ctx = useContext(EditorAiDataContext);
  if (!ctx) {
    throw new Error(
      'useEditorAiData must be used inside EditorAiDataProvider',
    );
  }
  return ctx;
};
